/**
 * The dashboard's Interventies section body: which interventie blocks to show.
 *
 * - No fase of this project carries the Odoo `Interventie` tag (every project
 *   until Igor tags one, and every old name-matched interventie): exactly the
 *   section as it always was — one project-wide block, uploads to the pile.
 * - One or more tagged fases: one block per tagged fase (upload into that fase,
 *   its own id-keyed photos only), then "Oudere interventiefoto's" for everything
 *   else — the legacy pile, the name-keyed folders, an untagged fase's folder.
 *   The older block stays viewable, deletable and re-datable, without upload.
 *
 * Billing is untouched: the vordering section still lists the whole project
 * prefix and groups by date, so every block's photos stay billable.
 */
import { useMemo } from 'react';
import type { DashboardFase } from '@/api/projectFases';
import type { PhotoItem, ProjectTask } from '@/types/odoo';
import { interventieFaseIds, splitInterventiePhotos } from '@/utils/interventiePhotos';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';
import { DashboardInterventieSection } from './DashboardInterventieSection';

interface DashboardInterventieBlocksProps {
  projectId: number;
  fases: DashboardFase[];
  projectTasks: ProjectTask[];
  planningDates?: Array<{ date: string; label: string }>;
  onChanged?: () => void;
}

export function DashboardInterventieBlocks({ projectId, fases, projectTasks, planningDates, onChanged }: DashboardInterventieBlocksProps) {
  // Tagged fases, in fase-bar order. A tagged fase not (yet) on the bar — no
  // planning child — still gets its block, after the listed ones.
  const tagged = useMemo(() => {
    const ids = interventieFaseIds(projectTasks);
    const listed = fases.filter((f) => ids.has(f.id)).map((f) => ({ id: f.id, name: f.name }));
    const rest = [...ids]
      .filter((id) => !listed.some((f) => f.id === id))
      .map((id) => ({ id, name: projectTasks.find((t) => t.id === id)?.name ?? `Fase ${id}` }));
    return [...listed, ...rest];
  }, [fases, projectTasks]);

  const taggedIds = useMemo(() => tagged.map((f) => f.id), [tagged]);
  const selectOlder = useMemo(
    () => (photos: PhotoItem[]) => splitInterventiePhotos(photos, taggedIds).older,
    [taggedIds],
  );

  if (tagged.length === 0) {
    return <DashboardInterventieSection projectId={projectId} planningDates={planningDates} onChanged={onChanged} embedded />;
  }

  return (
    <div className="space-y-6">
      {tagged.map((fase) => (
        <FaseBlock key={fase.id} projectId={projectId} fase={fase} planningDates={planningDates} onChanged={onChanged} />
      ))}
      <DashboardInterventieSection
        projectId={projectId}
        planningDates={planningDates}
        onChanged={onChanged}
        embedded
        allowUpload={false}
        hideWhenEmpty
        select={selectOlder}
        heading={<h4 className="text-sm font-extrabold text-td-muted">Oudere interventiefoto&apos;s</h4>}
      />
    </div>
  );
}

function FaseBlock({
  projectId,
  fase,
  planningDates,
  onChanged,
}: {
  projectId: number;
  fase: { id: number; name: string };
  planningDates?: Array<{ date: string; label: string }>;
  onChanged?: () => void;
}) {
  const select = useMemo(
    () => (photos: PhotoItem[]) => splitInterventiePhotos(photos, [fase.id]).byFase.get(fase.id) ?? [],
    [fase.id],
  );
  return (
    <DashboardInterventieSection
      projectId={projectId}
      planningDates={planningDates}
      onChanged={onChanged}
      embedded
      faseId={fase.id}
      select={select}
      heading={
        <h4 className="flex items-center gap-2 text-sm font-extrabold text-td-navy">
          {fase.name} <InterventieBadge />
        </h4>
      }
    />
  );
}
